<template>
  <div class="container-main">
    <div
      class="container-main__newsfeed newsfeed"
      :style="{ width: state.sumWidth + 'px' }"
    >
      <NewsPageToolbar
        v-if="newsFeedStore.totalCount >= 1 && isToolbarActive"
        :chosen-card-list="chosenCardList"
        :container="newsFeed"
        :is-all-page-card-chosen="isAllPageCardChosen"
        @reset-card-list="resetCardList"
        @toggle-all-cards="emit('toggle-all-cards')"
      />
      <div
        ref="newsFeed"
        class="newsfeed__wrapper scroll"
        :class="{ 'newsfeed__wrapper--toolbar-active': isToolbarActive }"
      >
        <NewsPageBodyNewsfeedItem
          v-for="(post, key) in newsFeedStore.items || []"
          :key="key"
          :box-width="state.width"
          :data="post"
          :index="key"
          :is-toolbar-active="isToolbarActive"
          :chosen-card-list="chosenCardList"
          @update:chosen-card-list="cards => emit('update:chosen-card-list', cards)"
        />
      </div>
      <div
        v-if="newsFeedStore.loading && newsFeedStore.totalCount >= 1"
        class="container-main__newsfeed--loader"
      />
      <NewsPagePagination
        v-if="newsFeedStore.totalCount >= 1"
        :container="newsFeed"
      />
    </div>
    <div
      v-if="newsFeedStore.items?.length"
      class="splitter"
      @mousedown="startDrag"
    />
  </div>
</template>

<script setup>
import NewsPageBodyNewsfeedItem from "@/components/NewsPage/NewsPageBody/NewsPageBodyNewsfeedItem.vue";
import { useNewsFeedStore } from "@/store/NewsFeed/newsFeed.js";
import {reactive, ref, onMounted, onBeforeUnmount, defineProps, defineEmits} from "vue";
import NewsPagePagination from "@/components/NewsPage/NewsPageRoof/NewsPagePagination.vue";
import NewsPageToolbar from "@/components/NewsPage/NewsPageRoof/NewsPageToolbar.vue";

const emit = defineEmits(["update:chosen-card-list", "toggle-all-cards"]);

const props = defineProps({
  isToolbarActive: {
    type: Boolean,
    required: true,
    default: false
  },
  chosenCardList: {
    type: Array,
    required: true,
    default: () => []
  },
  isAllPageCardChosen: {
    type: Boolean,
    default: false
  },
  parentContainerWidth: {
    type: Number,
    default: null
  }
});

const state = reactive({
  width: 471,
  mouseStart: 0,
  mouseDiff: 0,
  sumWidth: 471
});

const newsFeedStore = useNewsFeedStore();
const newsFeed = ref(null);

function throttle(func, limit) {
  let lastFunc;
  let lastRan;

  return function(...args) {
    if (!lastRan) {
      func.apply(this, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(function() {
        if ((Date.now() - lastRan) >= limit) {
          func.apply(this, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

const handleMouseMove = throttle((e) => {
  state.mouseDiff = e.clientX - state.mouseStart;
  const newWidth = state.width + state.mouseDiff;
  const maxWidth = (props.parentContainerWidth - 260) * 0.5; // 260px - ширина aside и отступы
  state.sumWidth = newWidth < 450 ? 450 : newWidth > maxWidth ? maxWidth : newWidth;
}, 22); // Throttle to 22ms

function startDrag(e) {
  state.mouseStart = e.clientX;
  document.addEventListener("mousemove", handleMouseMove);
  window.addEventListener("mouseup", stopDrag);
  newsFeedStore.pointer = true;
}

function stopDrag() {
  const maxWidth = (props.parentContainerWidth - 260) * 0.5;
  state.width = state.sumWidth < 450 ? 450 : state.sumWidth > maxWidth ? maxWidth : state.sumWidth;
  document.removeEventListener("mousemove", handleMouseMove);
  window.removeEventListener("mouseup", stopDrag);

  newsFeedStore.pointer = false;
  localStorage.setItem("widthContainer", state.width);
}

const resetCardList = () => {
  console.log("reset card list");
  emit("update:chosen-card-list", []);
};

// Инфинити-скролл: когда пользователь приближается к концу списка на INFINITE_THRESHOLD_PX,
// докидываем следующую страницу карточек. Кнопка «Показать ещё» в пагинаторе продолжает работать.
const INFINITE_THRESHOLD_PX = 300;
const onInfiniteScroll = throttle((e) => {
  const el = e.target || e;
  if (!el) return;
  const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
  if (distanceToBottom > INFINITE_THRESHOLD_PX) return;
  if (!newsFeedStore.hasMore || newsFeedStore.loading) return;
  newsFeedStore.loadMoreNews();
}, 150);

onMounted(() => {
  if (localStorage.getItem("widthContainer")) {
    state.sumWidth = Number(localStorage.getItem("widthContainer"));
    state.width = Number(localStorage.getItem("widthContainer"));
  }
  newsFeed.value?.addEventListener("scroll", onInfiniteScroll, { passive: true });
});

onBeforeUnmount(() => {
  newsFeed.value?.removeEventListener("scroll", onInfiniteScroll);
});
</script>

<style lang="scss" scoped>
.container-main {
  display: flex;
  height: 100%;
  position: relative;

  .splitter {
    cursor: col-resize;
    height: 100%;
    width: 10px;
    position: absolute;
    top: 0;
    right: -10px;
  }

  &__newsfeed {
    width: 100%;
    min-width: 450px;
    max-width: 100%;

    &--loader {
      top: 32px;
      right: 10px;
      position: absolute;
      width: calc(100% - 10px);
      height: 100%;
      background-color: rgba(255, 255, 255, 1);
      opacity: 40%;
    }
  }
}

.newsfeed {
  &__wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-height: 100%;
    max-height: calc(100vh - 14.9vh);

    padding: 0 5px 14px 0;

    &--toolbar-active {
      .newsfeed-item:first-child {
        margin-top: 40px;
      }
    }
  }

  &-item {
    &:last-child {
      margin-bottom: 25px;
    }
  }

  &__item {
    &:not(:last-child) {
      margin: 0 0 5px 0;
    }
  }

  &__loader {
    display: flex;
    justify-content: center;
    padding: 1.389vw 0;
    align-items: center;
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(100px, -50%);
  }
}
</style>
